import { action } from '@reatom/framework'

import { Data, pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { premiumMetaUpdateReceived } from '@root/entities/premium'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { ServicesErrorCode } from '@root/shared/api/api-error-code'

import type { Error } from '@root/shared'
import type { ServicesApi } from '@root/shared/api'

type CurrencySafetyCheck = {
  currency: ServicesApi.TokenMetaDto
  hasPassedSecurityChecks: boolean
  premiumInfo: ServicesApi.PremiumMetaDto | undefined
}

export class CurrencyNotFoundError extends Data.TaggedError(
  'CurrencyNotFoundError',
) {}

export type CurrencySafetyCheckResult = Result.Result<
  CurrencySafetyCheck,
  Error.UnknownError | CurrencyNotFoundError
>

export const checkCurrencyRequestAction = action(
  async (
    ctx,
    { address, network }: { address: string; network: Network.Network },
  ): Promise<CurrencySafetyCheckResult> => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesTokenSafetyCheckList, [
        {
          network: Network.toDto(network),
          token: address,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.map(
        (it): CurrencySafetyCheck => ({
          currency: it.tokenMeta,
          hasPassedSecurityChecks: it.isSafe,
          premiumInfo: it.premiumInfo,
        }),
      ),
      Result.mapError((e): Error.UnknownError | CurrencyNotFoundError =>
        isNotFoundError(e)
          ? new CurrencyNotFoundError()
          : ApiError.fallbackToUnknown(e),
      ),
    )

    if (Result.isSuccess(mappedResult) && mappedResult.success.premiumInfo) {
      premiumMetaUpdateReceived(ctx, {
        currency: mappedResult.success.currency,
        premiumMeta: mappedResult.success.premiumInfo,
      })
    }

    return mappedResult
  },
  'checkCurrencyRequestAction',
)

const isNotFoundError = (e: Error.UnknownError | ApiError.ApiError<string>) =>
  ApiError.isOfCode(ServicesErrorCode.TokenNotFound)(e)
